
import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/toast/index';
import { useNavigate } from "react-router-dom";

// Login form schema
const loginSchema = z.object({
  email: z.string().email({ message: "Ju lutem vendosni një email të vlefshëm" }),
  password: z.string().min(1, { message: "Ju lutem vendosni fjalëkalimin" }),
});

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onForgotPassword: () => void;
}

const LoginForm = ({ onSwitchToRegister, onForgotPassword }: LoginFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    try {
      await login(values.email, values.password);
      // Login successful - auth state change will handle redirect
    } catch (error) {
      // Error already handled in AuthContext
      console.error("Login error", error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hyni në llogarinë tuaj</CardTitle>
        <CardDescription>
          Vendosni email-in dhe fjalëkalimin tuaj për të hyrë në llogarinë tuaj.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="email@example.com" 
                      type="email"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fjalëkalimi</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input 
                        placeholder="••••••••" 
                        type={showPassword ? "text" : "password"}
                        {...field} 
                      />
                    </FormControl>
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex items-center justify-end">
              <Button 
                type="button" 
                variant="link" 
                className="p-0 h-auto text-sm"
                onClick={onForgotPassword}
                disabled={isLoading}
              >
                Keni harruar fjalëkalimin?
              </Button>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-brand-500 hover:bg-brand-600"
              disabled={isLoading}
            >
              {isLoading ? "Duke u kryer..." : "Hyni"}
            </Button>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button 
          variant="link" 
          onClick={onSwitchToRegister}
          disabled={isLoading}
        >
          Nuk keni një llogari? Regjistrohuni
        </Button>
      </CardFooter>
    </Card>
  );
};

export default LoginForm;
